/**
 * A simple notification.
 *
 * File: \src\components\messages\handler\UserNotification.ts
 * Created Date: Tuesday, May 28th 2019, 08:20:04
 * Author: Sascha Herrmann
 * -----
 * Last Modified: March 4th 2021, 13:35:34
 * Modified By: Sascha Herrmann
 * -----
 * Copyright (c) 2017-2019 Liebherr-Hausgeräte GmbH
 */

import { UserNotificationType } from './UserNotificationType';
import NotificationLink from './NotificationLink';

/**
 * Holds data for a user notification. A link can be shown.
 *
 * @export
 * @class UserNotification
 */
export default class UserNotification {
  /**
   * The text.
   *
   * @type {string}
   * @memberof UserNotification
   */
  public notificationText: string = '';

  /**
   * The type.
   *
   * @type {NotificationType}
   * @memberof UserNotification
   */
  public userNotificationType: UserNotificationType;

  /**
   * The link if given.
   *
   * @type {NotificationLink}
   * @memberof UserNotification
   */
  public notificationLink: NotificationLink;

  /**
   * Creates an instance of UserNotification.
   *
   * @param {string} text The text to show.
   * @param {UserNotificationType} type The type of message.
   * @param {NotificationLink} [link] A link to show. Optional.
   * @memberof UserNotification
   */
  constructor(text: string, type: UserNotificationType, link?: NotificationLink) {
    this.notificationText = text;
    this.userNotificationType = type;

    if (link) {
      this.notificationLink = link;
    }
  }

  /**
   * Method for snack notification. If it's true, UserSnack will be visualized.
   *
   * @returns {boolean}
   * @memberof UserNotification
   */
  public isSnack(): boolean {
    return this.userNotificationType === UserNotificationType.SNACK;
  }

  /**
   * Method for wanrings notification. If it's true, UserWarning will be visualized.
   *
   * @returns {boolean}
   * @memberof UserNotification
   */
  public isWarning(): boolean {
    return this.userNotificationType === UserNotificationType.WARNING;
  }

  /**
   * Method for error notification. If it's true, UserError will be visualized.
   *
   * @returns {boolean}
   * @memberof UserNotification
   */
  public isError(): boolean {
    return this.userNotificationType === UserNotificationType.ERROR;
  }

  /**
   * Method for error notification. If it's true, UserError will be visualized.
   *
   * @returns {boolean}
   * @memberof UserNotification
   */
  public isInternalError(): boolean {
    return this.userNotificationType === UserNotificationType.INTERNAL_ERROR;
  }
}
